import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { TicketEventType } from '@erp/shared';
import {
  ArrowRightLeft,
  Bot,
  CircleCheck,
  CircleX,
  CloudDownload,
  Flag,
  HardHat,
  ListChecks,
  ListX,
  PencilLine,
  MessageCircleReply,
  MessageSquare,
  Paperclip,
  Pause,
  Pencil,
  Play,
  RotateCcw,
  Send,
  Square,
  Ticket,
  TriangleAlert,
  UserRound,
  Users,
  Wrench,
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn, formatDateTime } from '@/lib/utils';
import { ticketEventsQuery } from './tickets-queries';
import { eventActorName, eventLabel } from './ticket-format';
import { eventDetailLines, type DetailLine } from './event-detail';
import { useTerms } from '@/shared/tenant-profile';

interface EventVisual {
  icon: React.ReactNode;
  className: string;
}

/** Glifo colorido por tipo de evento (padrão Tiflux: laranja=ticket, azul=apontamento/chat…). */
const EVENT_VISUALS: Record<TicketEventType, EventVisual> = {
  created: { icon: <Ticket />, className: 'bg-primary/10 text-primary' },
  updated: { icon: <Pencil />, className: 'bg-muted text-muted-foreground' },
  stage_changed: { icon: <Ticket />, className: 'bg-amber-50 text-amber-500' },
  status_changed: { icon: <Ticket />, className: 'bg-amber-50 text-amber-500' },
  priority_changed: { icon: <Flag />, className: 'bg-amber-50 text-amber-500' },
  responsible_changed: { icon: <UserRound />, className: 'bg-amber-50 text-amber-500' },
  reparented: { icon: <Users />, className: 'bg-amber-50 text-amber-500' },
  desk_moved: { icon: <ArrowRightLeft />, className: 'bg-amber-50 text-amber-500' },
  closed: { icon: <CircleCheck />, className: 'bg-emerald-50 text-emerald-600' },
  canceled: { icon: <CircleX />, className: 'bg-red-50 text-red-500' },
  reopened: { icon: <RotateCcw />, className: 'bg-blue-50 text-blue-600' },
  comment_added: { icon: <MessageSquare />, className: 'bg-blue-50 text-blue-600' },
  appointment_added: { icon: <ListChecks />, className: 'bg-blue-50 text-blue-600' },
  appointment_deleted: { icon: <ListX />, className: 'bg-muted text-muted-foreground' },
  // pedido da PoolTrack (02/10): apontamento editado, antes → depois
  appointment_edited: { icon: <PencilLine />, className: 'bg-blue-50 text-blue-600' },
  timer_started: { icon: <Play />, className: 'bg-primary/10 text-primary' },
  timer_stopped: { icon: <Square />, className: 'bg-primary/10 text-primary' },
  sla_paused: { icon: <Pause />, className: 'bg-blue-50 text-blue-600' },
  sla_resumed: { icon: <Play />, className: 'bg-blue-50 text-blue-600' },
  file_added: { icon: <Paperclip />, className: 'bg-blue-50 text-blue-600' },
  file_deleted: { icon: <Paperclip />, className: 'bg-muted text-muted-foreground' },
  service_order_created: { icon: <Wrench />, className: 'bg-primary/10 text-primary' },
  // spec 014 — disparo por ticket
  dispatch_sent: { icon: <Send />, className: 'bg-blue-50 text-blue-600' },
  dispatch_failed: { icon: <TriangleAlert />, className: 'bg-red-50 text-red-500' },
  dispatch_replied: { icon: <MessageCircleReply />, className: 'bg-emerald-50 text-emerald-600' },
  // spec 022 — nota do bot (API do bot)
  bot_note_added: { icon: <Bot />, className: 'bg-violet-50 text-violet-600' },
  // spec 026 — "Puxado do TiFlux por <admin>", com o que mudou
  tiflux_pulled: { icon: <CloudDownload />, className: 'bg-blue-50 text-blue-600' },
  // spec 043 — o espelho encerrou aqui o que já tinha acabado lá
  tiflux_closed: { icon: <CloudDownload />, className: 'bg-muted text-muted-foreground' },
  // técnico responsável em campo (pedido da PoolTrack, 02/10)
  technician_changed: { icon: <HardHat />, className: 'bg-amber-50 text-amber-500' },
};

/** tipo que esta versão da tela não conhece (a API sobe antes do SPA): glifo neutro, sem quebrar a aba */
const UNKNOWN_EVENT_VISUAL: EventVisual = { icon: <Pencil />, className: 'bg-muted text-muted-foreground' };

/** Aba "Histórico": timeline vertical com glifo por tipo e timestamp à direita. */
export function HistoricoTab({ ticketNumber }: { ticketNumber: number }) {
  const terms = useTerms();
  const eventsQ = useQuery(ticketEventsQuery(ticketNumber));

  const events = useMemo(
    () =>
      [...(eventsQ.data ?? [])].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ),
    [eventsQ.data]
  );

  return (
    <section className="rounded-lg border bg-card p-4 text-card-foreground shadow-sm">
      <h2 className="mb-4 text-base font-semibold">Histórico</h2>

      {eventsQ.isPending ? (
        <div className="space-y-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex gap-3">
              <Skeleton className="size-8 rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-3 w-64" />
              </div>
            </div>
          ))}
        </div>
      ) : eventsQ.isError ? (
        <p className="py-6 text-center text-sm text-muted-foreground">
          Erro ao carregar o histórico.
        </p>
      ) : events.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">Nenhum evento registrado.</p>
      ) : (
        <ol>
          {events.map((event, index) => {
            const visual = EVENT_VISUALS[event.type] ?? UNKNOWN_EVENT_VISUAL;
            const lines = eventDetailLines(event, terms);
            const isLast = index === events.length - 1;
            return (
              <li key={event.id} className="relative flex gap-3 pb-6 last:pb-0">
                {!isLast ? (
                  <span
                    aria-hidden
                    className="absolute top-9 bottom-0 left-4 w-px -translate-x-1/2 bg-border"
                  />
                ) : null}
                <span
                  className={cn(
                    'flex size-8 shrink-0 items-center justify-center rounded-full [&_svg]:size-4',
                    visual.className
                  )}
                >
                  {visual.icon}
                </span>
                <div className="min-w-0 flex-1 pt-0.5">
                  <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                    <span className="text-sm font-medium">{eventLabel(event.type)}</span>
                    <time className="text-xs whitespace-nowrap text-muted-foreground">
                      {formatDateTime(event.createdAt)}
                    </time>
                  </div>
                  {lines.length > 0 ? (
                    <ul className="space-y-0.5">
                      {lines.map((line, i) => (
                        <DetailLineItem key={i} line={line} />
                      ))}
                    </ul>
                  ) : null}
                  <p className="text-xs text-muted-foreground">{eventActorName(event)}</p>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}

/**
 * Uma linha do detalhe (pedido da PoolTrack, 03/10): "Telefone do solicitante:
 * (31) 98752-0000 → (31) 98752-4881". Cada lado tem teto; quando algum foi
 * cortado (a descrição, um título longo), o texto inteiro de antes e de depois
 * abre aqui — dá para recuperar uma ficha apagada por engano sem ir ao banco.
 */
function DetailLineItem({ line }: { line: DetailLine }) {
  const [open, setOpen] = useState(false);
  if (line.kind === 'text') return <li className="text-[13px] text-muted-foreground">{line.text}</li>;
  return (
    <li className="text-[13px] text-muted-foreground">
      <span className="font-medium text-foreground/80">{line.label}:</span>{' '}
      <span className="break-words">{line.from}</span> → <span className="break-words">{line.to}</span>
      {line.full ? (
        <>
          {' '}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className="text-xs font-medium text-primary hover:underline"
          >
            {open ? 'Esconder o antes e depois' : 'Ver o antes e depois'}
          </button>
          {open ? (
            <div className="mt-1 grid gap-2 sm:grid-cols-2">
              {(
                [
                  ['Antes', line.full.from],
                  ['Depois', line.full.to],
                ] as const
              ).map(([label, value]) => (
                <div key={label} className="min-w-0 rounded-md border bg-muted/40 p-2">
                  <p className="mb-1 text-[11px] font-semibold text-muted-foreground uppercase">{label}</p>
                  <p className="max-h-48 overflow-y-auto text-xs break-words whitespace-pre-wrap text-foreground">
                    {value ?? <span className="text-muted-foreground">(vazio)</span>}
                  </p>
                </div>
              ))}
            </div>
          ) : null}
        </>
      ) : null}
    </li>
  );
}
